import { useMemo } from 'react';

import { usePlayerResolvedAnimation } from '@cartwhl/3d/player';

import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import { getSourceBvhForTimeline } from '../keyPoseFrameMapping';
import {
  getMaxSourceFrameFromBvh,
  type KeyPoseIntervalAugmentationContext,
} from '../keyPoseIntervalAugmentation';
import { toIntervalSpacingConfig } from '../keyPoseIntervalAugmentationSettings';
import { resolveSelectedKeyPoseMotionForReference } from '../keyPoseMotionUtils';
import { useStore as useKeyPoseStore } from '../keyPoseStore';

function resolveActiveReferenceName(
  selectedReferenceName: string | null,
  sceneActors: readonly { referenceName: string }[],
  selectedCharacterId?: string | null,
) {
  return (
    selectedReferenceName ??
    (sceneActors.length <= 1
      ? (sceneActors[0]?.referenceName ?? null)
      : null) ??
    selectedCharacterId ??
    null
  );
}

export function useKeyPoseIntervalAugmentationContext():
  | KeyPoseIntervalAugmentationContext
  | null {
  const motionEditorStore = useMotionEditorStore();
  const keyPoseStore = useKeyPoseStore();
  const { intervalAugmentationSettings } = keyPoseStore.state;
  const { playback, characters } = motionEditorStore.state;
  const activeReferenceName = resolveActiveReferenceName(
    playback.selectedReferenceName,
    playback.sceneActors,
    characters.selectedCharacter?.characterID,
  );
  const keyPoseReferenceName =
    keyPoseStore.state.activeReferenceName ?? activeReferenceName;
  const resolvedAnimation = usePlayerResolvedAnimation(
    keyPoseReferenceName ?? undefined,
  );
  const selectedKeyPoseMotion = keyPoseReferenceName
    ? resolveSelectedKeyPoseMotionForReference(
        keyPoseReferenceName,
        playback.selectedReferenceName,
        playback.selectedSceneMotion,
        playback.sceneActors,
      )
    : null;
  const sourceBvh = getSourceBvhForTimeline(
    resolvedAnimation.bvh,
    resolvedAnimation.bvhs,
    selectedKeyPoseMotion?.timelineIndex,
  );

  return useMemo(() => {
    if (!sourceBvh) {
      return null;
    }

    return {
      bvh: sourceBvh,
      maxSourceFrame: getMaxSourceFrameFromBvh(sourceBvh),
      intervalFrames: intervalAugmentationSettings.intervalFrames,
      spacingConfig: toIntervalSpacingConfig(intervalAugmentationSettings),
    };
  }, [intervalAugmentationSettings, sourceBvh]);
}
